/**
 * Every feature switch and configurable number lives here with its default.
 * Values are stored in the `settings` table and edited from the admin dashboard;
 * these defaults are used until an admin changes them.
 */
export const DEFAULT_SETTINGS = {
  // Feature switches
  'feature.client_payment_enabled': true,
  'feature.manual_payment_enabled': true,
  'feature.chapa_payment_enabled': false,
  'feature.client_wallet_enabled': true,
  'feature.provider_subscription_enabled': false,
  'feature.agent_subscription_enabled': false,
  'feature.booking_deposit_enabled': false,
  'feature.promotions_enabled': false,
  'feature.referrals_enabled': true,
  'feature.chat_enabled': true,
  'feature.media_chat_enabled': true,
  'feature.provider_availability_enabled': true,
  'feature.reports_enabled': true,
  'feature.blocks_enabled': true,
  'feature.offline_telegram_notifications_enabled': true,
  'feature.onboarding_tutorial_enabled': true,
  'feature.partner_daily_report_enabled': true,
  'feature.experimentation_enabled': false,

  // Requests
  'requests.quota_per_period': 2,
  'requests.recharge_interval_hours': 12,
  'requests.expiry_minutes': 60,
  /** Return the credit to the client when a provider declines or a request expires. */
  'requests.refund_on_decline': true,

  // Sessions (minutes)
  'sessions.lengths': [60, 120],

  // Feedback
  'feedback.first_prompt_hours': 48,
  'feedback.retry_hours': 24,
  /** How many times a client is asked "Did you meet?" before we stop asking. */
  'feedback.max_attempts': 3,

  // Referrals
  /** Paid request credits the inviter gets for each friend who qualifies. */
  'referrals.reward_credits': 2,
  /** When a friend counts: when they finish registering, or when their first request is accepted. */
  'referrals.qualifying_event': 'FIRST_BOOKING' as 'REGISTERED' | 'FIRST_BOOKING',
  /** Most rewarded invites per person. */
  'referrals.max_per_user': 20,

  // Agents
  'agents.daily_report_time': '08:00' as string,
  'agents.subscription_price_per_provider_minor': 0,
  'agents.subscription_period_days': 30,

  // Chat
  /** Minimum minutes between two offline Telegram alerts for the same chat. */
  'chat.offline_alert_cooldown_minutes': 3,

  // Search
  'search.radius_options_km': [2, 5, 10, 25],
  'search.default_radius_km': 10,

  // Uploads
  'uploads.max_photo_mb': 10,
  'uploads.max_video_mb': 50,
  'uploads.max_voice_mb': 10,

  // General
  'general.default_timezone': 'Africa/Addis_Ababa',
  'general.currency': 'ETB',
  /** Telegram username (without @) that users can contact for support. Empty = bot inbox only. */
  'general.support_username': '' as string,
} as const;

export type SettingKey = keyof typeof DEFAULT_SETTINGS;
export type SettingValue<K extends SettingKey> = (typeof DEFAULT_SETTINGS)[K] extends boolean
  ? boolean
  : (typeof DEFAULT_SETTINGS)[K] extends number
    ? number
    : (typeof DEFAULT_SETTINGS)[K] extends string
      ? string
      : (typeof DEFAULT_SETTINGS)[K];

/** Settings safe to send to any signed-in user (the Mini App needs them to show/hide features). */
export const PUBLIC_SETTING_PREFIXES = ['feature.', 'sessions.', 'search.', 'uploads.', 'general.', 'referrals.'];

export function isPublicSetting(key: string): boolean {
  return PUBLIC_SETTING_PREFIXES.some((p) => key.startsWith(p));
}
